import { inject, Injectable, OnDestroy } from '@angular/core'
import { WindowRef } from '@spartacus/core'
import { fromEvent, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { TealiumUtagService } from './tealium/tealium.service'

@Injectable({
  providedIn: 'root'
})
export class LoginTrackerService implements OnDestroy {
  private destroy$ = new Subject<void>()
  private observer: MutationObserver | null = null
  private isTracking = false
  private trackedForms = new WeakSet()
  private windowRef = inject(WindowRef)

  constructor (private tealiumService: TealiumUtagService) {
    if (this.windowRef.isBrowser()) {
      this.initialize()
    }
  }

  private initialize (): void {
    if (!this.windowRef.isBrowser()) {
      return
    }

    if (this.observer) {
      this.observer.disconnect()
    }

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement
              const loginForms = [
                ...element.querySelectorAll('.gigya-login-form'),
                ...element.querySelectorAll('form[data-screenset-element-id*="login"]')
              ] as HTMLFormElement[]

              loginForms.forEach(form => this.setupFormTracking(form))
            }
          })
        }
      })
    })

    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    })

    this.setupExistingForms()
  }

  private setupExistingForms (): void {
    if (!this.windowRef.isBrowser()) {
      return
    }

    const loginForms = [
      ...document.querySelectorAll('.gigya-login-form'),
      ...document.querySelectorAll('form[data-screenset-element-id*="login"]')
    ] as HTMLFormElement[]

    loginForms.forEach(form => this.setupFormTracking(form))
  }

  private setupFormTracking (form: HTMLFormElement): void {
    if (!this.windowRef.isBrowser()) {
      return
    }

    if (this.trackedForms.has(form)) return

    this.trackedForms.add(form)
    const submitButton = form.querySelector('input[type="submit"], button[type="submit"]')

    if (!submitButton) {
      fromEvent(form, 'submit').pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: () => {
          this.tealiumService.trackLoginInitiation()
        },
        error: (err) => {
          console.error('Error tracking login form submit:', err)
        }
      })
    }
  }

  startTracking (): void {
    if (!this.windowRef.isBrowser()) {
      return
    }

    if (this.isTracking) return
    this.isTracking = true
    this.initialize()
  }

  stopTracking (): void {
    if (!this.windowRef.isBrowser()) {
      return
    }

    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    this.isTracking = false
  }

  ngOnDestroy (): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.stopTracking()
  }
}
